import {CircularProgress, InputAdornment} from '@mui/material';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate, useSearchParams} from 'react-router';
import Swal from 'sweetalert2';
import {
  errorBigIcon,
  gLogo,
  iconNotifySuccess,
  iconNotifyWarningInfo,
  lineArrowRight,
  toastSuccess,
  warningEmailBigIcon,
} from '../../../assets/icons/icons';
import ValidationErrorBlock from '../../../components/Blocks/ValidationErrorBlock/ValidationErrorBlock';
import IsVisibleButton from '../../../components/Buttons/IsVisibleButton/IsVisibleButton';
import {AuthWrapper as Wrapper, Button, FormControl, TextField} from '../../../components/StyledComponents';
import {EErrorText, EMAIL_REGEX, MAX_INPUT_VALUE_EMAIL_LENGTH, MAX_INPUT_VALUE_LENGTH,} from '../../../models/consts';
import {IError, ILogin} from '../../../models/inner-models';
import {login, loginGoogle, resendUserRegistrationLink} from '../../../store/actions';
import {useAppDispatch} from '../../../store/hooks';
import {decoratePopUpMessage} from '../../../utils/popUpTextDecorator';
import withRedirectIfAuth from '../../../utils/withRedirectIfAuthed';
import '../style.scss';
import {Helmet} from 'react-helmet';
import {fetchAuthSession, getCurrentUser, signInWithRedirect} from 'aws-amplify/auth';
import {Hub} from 'aws-amplify/utils';
import {Amplify} from 'aws-amplify';
import {iniFrame, setCookie} from "../../../serviceWorker";
import './style.scss';
import iconOkta from "../../../assets/icons/icon-okta.svg";
import AuthRightAnimation from "../../../components/AuthRightAnimation";
import AuthHeader from "../../../components/Header/AuthHeader";

const env = process.env.STAGE_TYPE;
const ALLOWED_ACTIVE_CAMPAIGN_ENVS = ['prod']

const Login: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { register, formState: { errors, isValid }, handleSubmit } = useForm<ILogin>({
    mode: 'onChange',
  });
  const [isProcessing, setProcessing] = useState<boolean>(false);
  const [visible, setPasswordVisibility] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [isGoogleAuth, setIsGoogleAuth] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  // Add this state to manage the Okta domain input
  const [isOkta, setIsOkta] = useState<boolean>(false);
  const [oktaDomain, setOktaDomain] = useState<string>('');
  const [oktaError, setOktaError] = useState<string>('');

  const isConfirmed = searchParams.get('isConfirmed');
  const extensionInstalled = searchParams.get('blextension');
  const handleClickShowPassword = () => () => {
    setPasswordVisibility(!visible);
  };

  // Function to handle the Okta domain input change
  const handleOktaDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!!oktaError) setOktaError('')
    setOktaDomain(event.target.value);
  };

  const getUser = async () => {
    setLoading(true)
    try {
      const currentUser = await getCurrentUser()
      const session = await fetchAuthSession()
      const isOktaToken = session.tokens?.idToken?.payload?.identities?.[0]?.providerName === 'okta'
      const payload = {
        token: session.tokens?.idToken?.toString(),
        isOkta: isOktaToken
      }
      await dispatch(loginGoogle(payload)).unwrap();

      if (localStorage.getItem('lastPage')) {
        window.location.assign(localStorage.getItem('lastPage') || '')
        localStorage.removeItem('lastPage')
      }
      setLoading(false)
      navigate('/');
    } catch(err) {
      setLoading(false)
      // console.log(err);
    }
  }

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signIn') {
        console.log('signin')
        return getUser();
      }
      if (payload.event === 'signOut') {
        console.log('signout')
      }
    });

    getUser();

    if (!!extensionInstalled) {
      setCookie('extensionInstalled', 1, 1)
    }

    if (isConfirmed) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
        title: translate('notifications.titles.success'),
        text: translate('notifications.auth.verification_successful'),
      });
    }

    return () => {
      unsubscribe();
    };
  }, []);

  const onSubmit: SubmitHandler<ILogin> = async (data: ILogin) => {
    const onResendLink = async () => {
      try {
        await dispatch(resendUserRegistrationLink(data.email))

        await Swal.fire({
          title: translate('notifications.titles.success'),
          text: decoratePopUpMessage(translate('notifications.auth.link_resent')),
          imageUrl: iconNotifySuccess,
        });
      } catch (e) {
        const error = e as IError;

        return Swal.fire({
          imageUrl: errorBigIcon,
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
        });
      }
    }

    setProcessing(true);
    try {
      await dispatch(login(data)).unwrap();

      if(env && ALLOWED_ACTIVE_CAMPAIGN_ENVS.includes(env)) {
        dataLayer.push({
          'event': 'log_in',
          'method': 'email',
          'user_data': {
            'email': data.email
          }
        });
      }
      if (localStorage.getItem('lastPage')) {
        window.location.assign(localStorage.getItem('lastPage') || '')
        localStorage.removeItem('lastPage')
      }
      navigate('/');
    } catch (err) {
      const error = err as IError;
      console.log(error);

      if (!error.error?.includes(EErrorText.NOT_CONFIRMED)) {
        return Swal.fire({
          imageUrl: iconNotifyWarningInfo,
          customClass: {
            popup: 'err-login-container',
            title: "swal-login-page"
          },
          title: 'The email or password is incorrect. Please try again',
          html: 'Sign up and <a href="/auth/sign-up">start your 14-day free trial</a> or try again',
          confirmButtonText: 'Sign up',
          showCancelButton: true,
          cancelButtonText: 'Try again'
        }).then((res) => {
            if (res.isConfirmed) {
              navigate('/auth/sign-up')
            }
          }
        );
      }

      Swal.fire({
        title: translate('notifications.titles.warning'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: warningEmailBigIcon,
        confirmButtonText: translate('notifications.choices.resend_verification'),
        showCancelButton: true,
        preConfirm: () => {
          window.location.hash = 'resend_verification'
          return onResendLink();
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });
    } finally {
      setProcessing(false);
    }
  };

  const googleAuth = async()=> {
    if (!isGoogleAuth) {
      setIsGoogleAuth(true)
      await signInWithRedirect({
        provider: 'Google'
      })
    }
  }

  const oktaAuth = ()=> {
    if (!isOkta) {
      setIsOkta(true)
    } else {
      handleOktaLogin()
    }
  }

  const handleOktaLogin = async () => {
    if (oktaDomain === 'moneysupermarketgroup.okta.com' || oktaDomain === 'moneysupermarketgroup') {
      // Call the Okta sign-in method for your company domain
      await handleOktaSignIn('1kloqmdhkiakct1gnofq6159ds'); //client ID
    } else if (oktaDomain === 'trial-3705378') {
      // Call the Okta sign-in method for your company domain
      await handleOktaSignIn('4srqhbqeeiqsbf1g4u0lo2ah15'); //client ID
    } else {
      // Handle other domains or show an error message
      setOktaError('Invalid Okta domain');
    }
  };

  const handleOktaSignIn = async (userPoolWebClientId: string) => {
    try {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
            userPoolClientId: userPoolWebClientId,
            loginWith: {
              oauth: {
                domain: process.env.REACT_APP_COGNITO_DOMAIN,
                scopes: [
                  'email',
                  'openid',
                  'profile',
                ],
                redirectSignIn: [userPoolWebClientId === '4srqhbqeeiqsbf1g4u0lo2ah15' ? 'https://a49y3x27y2.execute-api.us-west-2.amazonaws.com/prod/auth/okta/callback/okta-test' : `${process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN}/auth/login?okta=okta.monygroup.com`],
                redirectSignOut: [`${process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN}/auth/login`],
                responseType: 'token' as const,
                providers: ['Google', 'okta'] as const
              }
            }
          }
        }
      });
      await signInWithRedirect({provider: 'okta'});
    } catch (error) {
      console.log("Error signing in:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title>BackupLABS Login Page</title>
        <meta name="title" content="BackupLABS Login Page" />
        <meta name="description" content="Log into the BackupLABS SaaS app backup platform." />
      </Helmet>
      <div className="auth-login auth-login-page">
        <div className="auth-login-left">
          <AuthHeader/>
          <Wrapper className="auth-new-form">
            {loading ?
              <div className="spinner-wrapper">
                <CircularProgress className="login__spinner" />
              </div> :
              <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <div className="auth-title">
                  {translate('common.default_pages.sign_in')}
                </div>
                {!isOkta && (
                  <div className="login__subtitle links-block">
                    <span className="login__subtitle-prefix">{translate('forms.sign_in.dont_have_acc')}</span>{' '}
                    <Link to="/auth/sign-up" className="link">
                      {translate('forms.sign_in.trial')}
                    </Link>
                  </div>
                )}
                {!iniFrame() && !isOkta && (
                  <div className="auth-oauth-row">
                    <button
                      type="button"
                      className={`google-btn auth-oauth-btn ${isGoogleAuth ? 'disabled' : ''}`}
                      onClick={googleAuth}
                    >
                      <div className="google-icon-wrapper">
                        <img className="google-icon-svg" src={gLogo} alt="" loading="lazy" />
                      </div>
                      <p className="btn-text">
                        <span>{translate('forms.sign_in.sign_in_with_google')}</span>
                      </p>
                    </button>
                    <button
                      type="button"
                      className={`google-btn auth-oauth-btn auth-oauth-btn--okta ${isGoogleAuth ? 'disabled' : ''}`}
                      onClick={oktaAuth}
                    >
                      <div className="okta-icon-wrapper">
                        <img className="okta-icon-svg" src={iconOkta} alt="" loading="lazy" />
                      </div>
                      <p className="btn-text">
                        <span>{translate('forms.sign_in.sign_in_with_okta')}</span>
                      </p>
                    </button>
                  </div>
                )}
                {!iniFrame() && !isOkta && (
                  <div className="auth-or-separator auth-or-separator--login">
                    <span>OR</span>
                  </div>
                )}
                {!isOkta && (
                  <div>
                    <FormControl className="form-row login__email-row">
                      <div className="auth-form__label-row">
                        <div className="label-block">{translate('forms.common.emailA')}</div>
                        {!!errors.email?.message && (
                          <span className="auth-form__field-error">{errors.email.message}</span>
                        )}
                      </div>
                      <div className="auth-form__input-block">
                        <TextField
                          required
                          placeholder={translate('forms.common.email')}
                          className="auth-mui-textfield"
                          id="email"
                          type="email"
                          inputProps={{ maxLength: MAX_INPUT_VALUE_EMAIL_LENGTH }}
                          {...register('email', {
                            required: translate('forms.common.required') as string,
                            pattern: {
                              value: EMAIL_REGEX,
                              message: translate('forms.common.invalid_email'),
                            },
                          })}
                          error={!!errors.email}
                        />
                      </div>
                    </FormControl>
                  </div>
                )}
                {!isOkta && (
                  <div>
                    <FormControl className="form-row">
                      <div className="login__password-row">
                        <div className="label-block">{translate('forms.common.password')}</div>
                        <Link to="/auth/password-recovery" className="link login__forgot-link">
                          {translate('forms.sign_in.forgot_password')}
                        </Link>
                      </div>
                      <div className="auth-form__input-block">
                        <TextField
                          className="auth-mui-passfield"
                          placeholder={translate('forms.common.password')}
                          required
                          id="password"
                          type={visible ? 'text' : 'password'}
                          {...register('password', {
                            required: translate('forms.common.required') as string,
                          })}
                          error={!!errors.password}
                          inputProps={{ maxLength: MAX_INPUT_VALUE_LENGTH }}
                          InputProps={{
                            endAdornment:
                              <InputAdornment position="end">
                                <IsVisibleButton
                                  isPrimary
                                  onClick={handleClickShowPassword()}
                                  isNowVisible={visible}
                                />
                              </InputAdornment>,
                          }}
                        />
                        {errors.password &&
                          <ValidationErrorBlock errorMessage={errors.password.message as string} />
                        }
                      </div>
                    </FormControl>
                  </div>
                )}
                {isOkta && (
                  <div className='okta-input-domain'>
                    <FormControl className="form-row">
                      <div className="label-block full-width">Your Okta Company Domain</div>
                      <div className="auth-form__input-block">
                        <TextField
                          required
                          placeholder='Okta Company Domain'
                          className="auth-mui-textfield"
                          id="email"
                          type="text"
                          value={oktaDomain}
                          onChange={handleOktaDomainChange}
                          inputProps={{ maxLength: 25 }}
                          error={!!oktaError}
                        />
                        {!!oktaError &&
                          <ValidationErrorBlock errorMessage={oktaError} />
                        }
                      </div>
                    </FormControl>
                  </div>
                )}
                <div className="action-row">
                  {!isOkta && (
                    <Button
                      className={`primary-button login__sign-in-btn${isProcessing ? ' login__sign-in-btn--processing' : ''}`}
                      type="submit"
                      disabled={!isValid || isProcessing || isGoogleAuth}>
                      {isProcessing ? (
                        <>
                          <div className="small-spinner-wrapper">
                            <CircularProgress color="inherit" className="auth-btn-spinner" />
                          </div>
                          {translate('notifications.choices.processing')}
                        </>
                      ) : (
                        <span className="auth-submit-inner auth-submit-inner--gap-lg">
                          {translate('common.default_pages.sign_in')}
                          <img src={lineArrowRight} alt="" className="auth-submit-arrow" width={20} height={20} />
                        </span>
                      )}
                    </Button>
                  )}
                  {!iniFrame() && isOkta && (
                    <Button className="login__full-width-button" onClick={() => setIsOkta(false)} variant="outlined"
                            color="primary" mt={4}>
                      {translate('notifications.choices.cancel')}
                    </Button>
                  )}
                </div>
              </form>
            }
          </Wrapper>
        </div>
        <div className="auth-login-right">
          <AuthRightAnimation/>
        </div>
      </div>
    </>
  );
};

export default withRedirectIfAuth()(Login);
